import { useEffect, useState } from 'react';
import useIsLandscape from './useIsLandscape';

/**
 * The URL to paint a backoffice background image with. It asks the CDN for a
 * screen-sized copy via the `/WxH` suffix (same convention as item images), but
 * the resizer fails on some uploads (e.g. certain PNGs return a 500). The sized
 * URL is preloaded, and if it fails the original, unresized URL is used instead
 * so the stage never ends up plain black.
 */
const useBackgroundImageUrl = (src: string | null): string | null => {
  const isLandscape = useIsLandscape();

  const sizedUrl = src ? `${src}/${isLandscape ? '3840x2160' : '2160x3840'}` : null;

  /** The sized URL that failed to load, so an orientation change retries with the new size. */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!sizedUrl) {
      return undefined;
    }

    const image = new Image();
    image.onerror = () => {
      setFailedUrl(sizedUrl);
    };
    image.src = sizedUrl;

    /** Ignore a late error from a URL that is no longer the current one. */
    return () => {
      image.onerror = null;
    };
  }, [sizedUrl, src]);

  if (!src || !sizedUrl) {
    return null;
  }

  return failedUrl === sizedUrl ? src : sizedUrl;
};

export default useBackgroundImageUrl;
